import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { PlusIcon } from '@heroicons/react/24/outline';
import ReviewsList from './ReviewsList';
import ReviewSubmissionForm, { ReviewSubmissionData } from './ReviewSubmissionForm';
import { Review } from './ReviewCard';
import { appConfig } from '../../config/appConfig';
import { api } from '../../services/api';
import { toast } from 'sonner';

export interface ReviewsContainerProps {
  promptId: number;
  promptTitle?: string;
  isAuthenticated?: boolean;
  reviewerName?: string;
  onReviewCountChange?: (count: number) => void;
}

interface ReviewsState {
  reviews: Review[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  sortBy: 'newest' | 'oldest' | 'helpful';
  isLoading: boolean;
  error: string | null;
  userHelpfulVotes: { [reviewId: number]: boolean | null };
}

const API_BASE_URL = appConfig.apiUrl;

export const ReviewsContainer: React.FC<ReviewsContainerProps> = ({
  promptId,
  promptTitle,
  isAuthenticated,
  reviewerName,
  onReviewCountChange
}) => {
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [userHasReviewed, setUserHasReviewed] = useState(false);
  const [state, setState] = useState<ReviewsState>({
    reviews: [],
    totalCount: 0,
    currentPage: 1,
    pageSize: 10,
    sortBy: 'newest',
    isLoading: true,
    error: null,
    userHelpfulVotes: {}
  });

  const fetchReviews = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const params = new URLSearchParams({
        page: state.currentPage.toString(),
        pageSize: state.pageSize.toString(),
        sortBy: state.sortBy
      });

      const response = await fetch(`${API_BASE_URL}/reviews/prompt/${promptId}?${params}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch reviews: ${response.statusText}`);
      }

      const data = await response.json();
      const reviewCount = data.TotalCount || data.totalCount || 0;

      setState(prev => ({
        ...prev,
        reviews: data.Reviews || data.reviews || [],
        totalCount: reviewCount,
        isLoading: false
      }));

      if (onReviewCountChange) {
        onReviewCountChange(reviewCount);
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load reviews'
      }));
    }
  }, [promptId, state.currentPage, state.pageSize, state.sortBy, onReviewCountChange]);

  // TODO: Implement helpful votes functionality if needed
  // const fetchUserHelpfulVotes = async () => { ... };

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleSubmitReview = useCallback(async (data: ReviewSubmissionData) => {
    if (!isAuthenticated) {
      throw new Error('You must be logged in to submit a review');
    }

    setSubmissionLoading(true);
    setSubmissionError(null);

    try {
      await api.submitReview({
        promptId,
        reviewerName: reviewerName || 'Anonymous',
        title: data.title,
        content: data.content,
      });

      setShowSubmissionForm(false);
      setUserHasReviewed(true);
      toast.success('Review submitted! It will appear after moderation.');
      await fetchReviews();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit review';
      setSubmissionError(errorMessage);
      throw error;
    } finally {
      setSubmissionLoading(false);
    }
  }, [isAuthenticated, reviewerName, promptId, fetchReviews]);

  const handlePageChange = useCallback((page: number) => {
    setState(prev => ({ ...prev, currentPage: page }));
  }, []);

  const handleSortChange = useCallback((sortBy: 'newest' | 'oldest' | 'helpful') => {
    setState(prev => ({ ...prev, sortBy, currentPage: 1 }));
  }, []);

  const handleHelpfulClick = useCallback(async (reviewId: number, isHelpful: boolean) => {
    if (!isAuthenticated) {
      alert('You must be logged in to vote on review helpfulness');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/reviews/${reviewId}/helpful`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isHelpful }),
      });

      if (!response.ok) {
        throw new Error('Failed to record vote');
      }

      // Update local state
      setState(prev => ({
        ...prev,
        userHelpfulVotes: {
          ...prev.userHelpfulVotes,
          [reviewId]: isHelpful
        },
        reviews: prev.reviews.map(review => {
          if (review.id === reviewId) {
            const currentVote = prev.userHelpfulVotes[reviewId];
            let helpfulCount = review.helpfulCount;
            let notHelpfulCount = review.notHelpfulCount;

            // Remove previous vote
            if (currentVote === true) helpfulCount--;
            if (currentVote === false) notHelpfulCount--;

            // Add new vote
            if (isHelpful) helpfulCount++;
            else notHelpfulCount++;

            return { ...review, helpfulCount, notHelpfulCount };
          }
          return review;
        })
      }));
    } catch (error) {
      console.error('Error voting on review:', error);
      alert('Failed to record vote. Please try again.');
    }
  }, [isAuthenticated]);

  if (state.error) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="text-red-600">
            <h3 className="text-lg font-medium mb-2">Error Loading Reviews</h3>
            <p>{state.error}</p>
            <Button 
              onClick={() => fetchReviews()} 
              className="mt-4"
              variant="outline"
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with average rating and submit button */}
      <Card>
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">
                Reviews
                {promptTitle && <span className="text-lg text-gray-600 font-normal"> for "{promptTitle}"</span>}
              </h2>
              
              {state.totalCount > 0 && (
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold">{state.totalCount} review{state.totalCount !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>

            {isAuthenticated && !showSubmissionForm && !userHasReviewed && (
              <Button
                onClick={() => {
                  setShowSubmissionForm(true);
                  setSubmissionError(null);
                }}
                className="flex items-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                Write Review
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Review Submission Form */}
      {showSubmissionForm && (
        <div className="space-y-4">
          {submissionError && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="py-4">
                <div className="flex items-center gap-2 text-red-800">
                  <div className="text-sm">
                    <strong>Error submitting review:</strong> {submissionError}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          <ReviewSubmissionForm
            promptId={promptId}
            onSubmit={handleSubmitReview}
            onCancel={() => {
              setShowSubmissionForm(false);
              setSubmissionError(null); // Clear error when canceling
            }}
            isLoading={submissionLoading}
          />
        </div>
      )}

      {/* Reviews List */}
      <ReviewsList
        promptId={promptId}
        reviews={state.reviews}
        totalCount={state.totalCount}
        currentPage={state.currentPage}
        pageSize={state.pageSize}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        onHelpfulClick={handleHelpfulClick}
        isLoading={state.isLoading}
        currentUserHelpfulVotes={state.userHelpfulVotes}
      />
    </div>
  );
};

export default ReviewsContainer;